import React, { useEffect, useMemo, useState } from 'react';
import styles from './Filters.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getSportTypes } from '../../../store/actions/sportTypes';
import { getSeasons } from '../../../store/actions/seasons';
import { useEffectSkipFirst } from '../../../hooks/useEffectSkipFirst';
import Select from 'react-select';
import Categories from './Categories';
import Leagues from './Leagues';
import moment from 'moment';
import { getTranslation } from '../../../utils/translation';
import { LANGUAGES } from '../../../constants/defaultValues';

const initialSeasons = [{ id: 'all', name: getTranslation('resultsFiltersAllLeagues') }];
const initialCategories = [{ id: 'all', name: getTranslation('resultsFiltersAllCategories') }];

export default function ResultsFilter({ periods, orders, changeFilter, filterParams }) {
    const dispatch = useDispatch();
    const { locale } = useSelector(state => state.settings);
    const sportTypes = useSelector(state => state.sportTypes.sportsList);
    const filtersList = useSelector(state => state.seasons.list);
    const { eventType } = useSelector(state => state.sportEvents);

    const [sports, setSports] = useState([]);
    const [categories, setCategories] = useState([]);
    const [leagues, setLeagues] = useState([]);
    const [selectedSport, setSelectedSport] = useState();
    const [selectedPeriod, setSelectedPeriod] = useState(periods[0]);
    const [selectedOrder, setSelectedOrder] = useState(orders[0]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(initialCategories[0].id);
    const [selectedLeagueId, setSelectedLeagueId] = useState(initialSeasons[0].id);

    useEffect(() => {
        const { startDate, endDate } = getSeasonsDates();

        dispatch(getSportTypes({ isLive: false, eventType: eventType.value }));
        dispatch(
            getSeasons({
                sportTypeId: filterParams.sportTypeId,
                startDate,
                endDate,
                pageNumber: 0,
            })
        );
    }, []);

    useEffect(() => {
        if (sportTypes) {
            const updatedSportTypes = sportTypes
                .filter(sport => sport.referenceId !== '0:bt:sport:1')
                .map(item => ({
                    ...item,
                    value: item.referenceId,
                    label: item.names[locale] || item.names[LANGUAGES.eng],
                }));

            setSports(updatedSportTypes);
            setSelectedSport(updatedSportTypes[0]);
        }
    }, [sportTypes]);

    useEffectSkipFirst(() => {
        const { startDate, endDate } = getSeasonsDates();

        dispatch(
            getSeasons({
                sportTypeId: filterParams.sportTypeId,
                startDate,
                endDate,
                pageNumber: 0,
            })
        );
    }, [filterParams.sportTypeId]);

    useEffect(() => {
        setCategories(initialCategories.concat(filtersList.categories || []));
        const found = selectedCategoryId === 'all' || filtersList.categories?.find(c => c.id === selectedCategoryId);
        if (!found) {
            setSelectedCategoryId('all');
            changeFilter('category', { value: 'all' });
        }
    }, [filtersList.categories]);

    useEffect(() => {
        setLeagues(initialSeasons.concat(filtersList.seasons || []));
        const found = selectedLeagueId === 'all' || filtersList.seasons?.find(s => s.id === selectedLeagueId);
        if (!found) {
            setSelectedLeagueId('all');
            changeFilter('league', { value: 'all' });
        }
    }, [filtersList.seasons]);

    const getSeasonsDates = () => {
        const startDate = moment().subtract(1, 'weeks').startOf('isoWeek').valueOf();
        const endDate = moment().valueOf();

        return { startDate, endDate };
    };

    const onSportChange = item => {
        changeFilter('sport', item);

        setSelectedSport(item);
    };

    const onPeriodChange = item => {
        changeFilter('period', item);

        setSelectedPeriod(item);
    };

    const onOrderChange = item => {
        changeFilter('order', item);

        setSelectedOrder(item);
    };

    const onCategoryChange = item => {
        changeFilter('category', item);
        changeFilter('league', { value: 'all' });

        setSelectedCategoryId(item.value);
        setSelectedLeagueId('all');
    };

    const onSeasonChange = item => {
        changeFilter('league', item);

        if (item.categoryId) {
            setSelectedCategoryId(item.categoryId || 'all');
            changeFilter('category', { value: item.categoryId || 'all' });
        }
        setSelectedLeagueId(item.value);
    };

    return (
        <div className={styles.filters}>
            <div className={'filter-area'}>
                {sports.length > 0 && (
                    <>
                        <div className={'filter-grid'} style={{ width: '200px' }}>
                            <Select
                                className='common-select common-select--events'
                                classNamePrefix='common'
                                menuPlacement={'auto'}
                                value={selectedSport}
                                onChange={onSportChange}
                                options={sports}
                                noOptionsMessage={getTranslation('noOptionsMessage')}
                            />
                        </div>

                        <div className={'filter-grid'} style={{ width: '15%' }}>
                            <Categories
                                categories={categories}
                                selectedCategoryId={selectedCategoryId}
                                onChange={onCategoryChange}
                            />
                        </div>

                        <div className={'filter-grid'} style={{ width: '20%' }}>
                            <Leagues
                                leagues={leagues}
                                selectedLeagueId={selectedLeagueId}
                                selectedCategoryId={selectedCategoryId}
                                onChange={onSeasonChange}
                            />
                        </div>

                        <div className={'filter-grid'} style={{ width: '180px' }}>
                            <Select
                                className='common-select common-select--events'
                                classNamePrefix='common'
                                menuPlacement={'auto'}
                                options={periods}
                                getOptionLabel={option => option.value}
                                value={selectedPeriod}
                                onChange={onPeriodChange}
                                noOptionsMessage={getTranslation('noOptionsMessage')}
                            />
                        </div>

                        <div className={'filter-grid'} style={{ width: '150px' }}>
                            <Select
                                className='common-select common-select--events'
                                classNamePrefix='common'
                                menuPlacement={'auto'}
                                options={orders}
                                value={selectedOrder}
                                onChange={onOrderChange}
                                noOptionsMessage={getTranslation('noOptionsMessage')}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
